import { CloudIcon } from '@heroicons/react/24/outline';
import { ProviderImage } from '../provider-image';
import { CODE_PROVIDERS } from '../../constants/code-provider';
import { MultiSelect, MultiSelectProps, SelectMode } from './multi-select';

import type { JSX } from 'react';

import { useTranslation } from 'react-i18next';

export type ProviderFilterOptions = {
  value: string;
  searchString: string;
  label: JSX.Element | string;
  selectedDisplay: JSX.Element | string;
};

const DEFAULT_OPTIONS: ProviderFilterOptions[] = [
  {
    value: 'AWS',
    searchString: 'Amazon Web Services',
    label: (
      <>
        <ProviderImage sizeClassName="h-6 w-8 flex-shrink-0" provider={'AWS'} />
        Amazon Web Services
      </>
    ),
    selectedDisplay: (
      <div className="flex items-center gap-1">
        <ProviderImage sizeClassName="w-8 h-6 flex-shrink-0" provider={'AWS'} />
        <div className="truncate">AWS</div>
      </div>
    ),
  },
  {
    value: 'Azure',
    searchString: 'Microsoft Azure',
    label: (
      <>
        <ProviderImage
          sizeClassName="h-6 w-8 flex-shrink-0"
          provider={'Azure'}
        />
        Microsoft Azure
      </>
    ),
    selectedDisplay: (
      <div className="flex items-center gap-1">
        <ProviderImage
          sizeClassName="w-8 h-6 flex-shrink-0"
          provider={'Azure'}
        />
        <div className="truncate">Azure</div>
      </div>
    ),
  },
  {
    value: 'GCP',
    searchString: 'Google Cloud',
    label: (
      <>
        <ProviderImage sizeClassName="h-6 w-8 flex-shrink-0" provider={'GCP'} />
        Google Cloud
      </>
    ),
    selectedDisplay: (
      <div className="flex items-center gap-1">
        <ProviderImage sizeClassName="w-8 h-6 flex-shrink-0" provider={'GCP'} />
        <div className="truncate">GCP</div>
      </div>
    ),
  },
  {
    value: 'Kubernetes',
    searchString: 'Kubernetes',
    label: (
      <>
        <ProviderImage
          sizeClassName="h-6 w-8 flex-shrink-0"
          provider={'Kubernetes'}
        />
        Kubernetes
      </>
    ),
    selectedDisplay: (
      <div className="flex items-center gap-1">
        <ProviderImage
          sizeClassName="w-8 h-6 flex-shrink-0"
          provider={'Kubernetes'}
        />
        <div className="truncate">Kubernetes</div>
      </div>
    ),
  },
];

/**
 * The cloud providers plus the providers that host code assets, for pages that
 * list both.
 */
export const ASSET_PROVIDER_OPTIONS: ProviderFilterOptions[] = [
  ...DEFAULT_OPTIONS,
  ...CODE_PROVIDERS.map((provider) => ({
    value: provider,
    searchString: provider,
    label: (
      <>
        <ProviderImage
          sizeClassName="h-6 w-8 flex-shrink-0"
          provider={provider}
        />
        {provider}
      </>
    ),
    selectedDisplay: (
      <div className="flex items-center gap-1">
        <ProviderImage
          sizeClassName="w-8 h-6 flex-shrink-0"
          provider={provider}
        />
        <div className="truncate">{provider}</div>
      </div>
    ),
  })),
];

export const ProviderMultiSelect = ({
  icon,
  label,
  setValue,
  value,
  providerOptions,
  dataTestId,
  ...props
}: Omit<
  MultiSelectProps<(typeof DEFAULT_OPTIONS)[number]>,
  'options' | 'mode' | 'label'
> &
  SelectMode & {
    label?: string;
    providerOptions?: ProviderFilterOptions[];
  }) => {
  const { t } = useTranslation('designSystem');
  return (
    <MultiSelect
      icon={icon || (CloudIcon as React.FC<{ className?: string }>)}
      label={label || t('common:provider')}
      options={providerOptions || DEFAULT_OPTIONS}
      displaySelected
      setValue={setValue as any}
      value={value as any}
      dataTestId={dataTestId}
      {...props}
    />
  );
};
